import { useCallback, useEffect, useRef, useState } from "react";

import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";

import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { getAdminSettings } from "sj-ui-toolkit/src/api/admin/common";
import { setAdminSettings } from "sj-ui-toolkit/src/api/admin/settings";
import { getSiteSettings } from "sj-ui-toolkit/src/api/siteSettings";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";

const WebhooksConnection = () => {
  useBreadcrumb({
    label: "Webhooks",
    route: AdminAppRoutes.routes.connections.routes.webhooks.route,
    display: "page",
  });

  const toast = useRef<Toast>(null);

  const [enabled, setEnabled] = useState(false);
  const [endpointUrl, setEndpointUrl] = useState("");
  const [savedEndpointUrl, setSavedEndpointUrl] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isToggling, setIsToggling] = useState(false);

  const showLocalToast = useCallback(
    (
      severity: "success" | "info" | "warn" | "error",
      summary: string,
      detail: string,
      life = 3000
    ) => {
      toast.current?.show({ severity, summary, detail, life });
    },
    []
  );

  useEffect(() => {
    const load = async () => {
      try {
        const [adminData, siteData] = await Promise.all([getAdminSettings(), getSiteSettings()]);
        const url = siteData?.site?.enrollmentCompletionNotificationEndpoint || "";
        setEnabled(!!adminData?.webhookEnabled);
        setEndpointUrl(url);
        setSavedEndpointUrl(url);
      } catch (error) {
        console.error("Failed to load webhooks settings", error);
        showLocalToast("error", "Error", "Failed to load webhooks settings", 5000);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [showLocalToast]);

  const handleToggle = async () => {
    setIsToggling(true);
    const nextEnabled = !enabled;
    try {
      const result = await setAdminSettings({
        name: "webhookEnabled",
        value: nextEnabled ? "true" : "",
      });
      if (!result) throw new Error("Failed to update webhooks setting");
      setEnabled(nextEnabled);
      showLocalToast(
        "success",
        "Success",
        `Webhooks ${nextEnabled ? "enabled" : "disabled"} successfully`
      );
    } catch (error) {
      console.error("Failed to toggle webhooks", error);
      showLocalToast("error", "Error", "Failed to update webhooks setting");
    } finally {
      setIsToggling(false);
    }
  };

  const handleEndpointBlur = async () => {
    if (endpointUrl === savedEndpointUrl) return;
    try {
      const result = await setAdminSettings(
        { name: "enrollmentCompletionNotificationEndpoint", value: endpointUrl },
        "set"
      );
      if (!result) throw new Error("Failed to update webhook URL");
      setSavedEndpointUrl(endpointUrl);
      showLocalToast("success", "Success", "Webhook URL updated successfully");
    } catch (error) {
      console.error("Failed to update webhook endpoint", error);
      showLocalToast("error", "Error", "Failed to update webhook URL");
    }
  };

  return (
    <>
      <Toast ref={toast} />
      <div className="mb-4">
        <h1>Webhooks</h1>
      </div>

      <Card>
        <div className="flex justify-between items-center mb-4">
          <h2 className="atlas-h4">Webhook Settings</h2>
          {isLoading ? (
            <ProgressSpinner style={{ width: "30px", height: "30px" }} />
          ) : (
            <InputSwitch
              inputId="webhook-enabled"
              checked={enabled}
              onChange={handleToggle}
              disabled={isToggling}
              aria-label="Enable webhooks"
            />
          )}
        </div>

        <div className="flex flex-col gap-3">
          <label htmlFor="webhookUrl" className="block font-medium">
            Enrollment Completion Notification URL
          </label>
          <InputText
            id="webhookUrl"
            value={endpointUrl}
            onChange={e => setEndpointUrl(e.target.value)}
            onBlur={handleEndpointBlur}
            disabled={!enabled || isLoading}
            placeholder="https://your-webhook-endpoint.com/notify"
            className="w-full"
            aria-required="true"
          />
          <p className="body-sm text-secondary italic">
            When enabled, a webhook notification will be sent to this URL when a user completes an
            enrollment.
          </p>
        </div>
      </Card>
    </>
  );
};

export default WebhooksConnection;
